import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Tag } from 'lucide-react';
import { eventService } from '../services/eventService';
import { Button, Input, LoadingSpinner, FeaturedImageUpload } from '../components/ui';
import { toast } from '../components/ui/Toaster';

interface EventFormData {
  title: string;
  description: string;
  type: string;
  location: string;
  startDate: string;
  endDate: string;
  featuredImage?: File | null;
}

const CreateEventPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
    type: 'FESTIVAL',
    location: '',
    startDate: '',
    endDate: '',
    featuredImage: null,
  });

  const eventTypes = [
    { value: 'FESTIVAL', label: 'Festival' },
    { value: 'CONCERT', label: 'Concert' },
    { value: 'PARTY', label: 'Feest' },
    { value: 'CONFERENCE', label: 'Conferentie' },
    { value: 'MEETUP', label: 'Bijeenkomst' },
    { value: 'WORKSHOP', label: 'Workshop' },
    { value: 'SPORT', label: 'Sport' },
    { value: 'OTHER', label: 'Anders' },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileSelect = (file: File | null) => {
    setFormData(prev => ({
      ...prev,
      featuredImage: file,
    }));
    
    // Clear error when file is selected
    if (errors.featuredImage) {
      setErrors(prev => ({ ...prev, featuredImage: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Evenement naam is vereist';
    } else if (formData.title.length < 3) {
      newErrors.title = 'Evenement naam moet minstens 3 tekens bevatten';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Locatie is vereist';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Startdatum is vereist';
    }

    // Date validation
    if (formData.startDate) {
      const startDate = new Date(formData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      
      if (startDate < today) {
        newErrors.startDate = 'Startdatum moet vandaag of in de toekomst liggen';
      }
    }

    // End date validation
    if (formData.endDate && formData.startDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      
      if (endDate < startDate) {
        newErrors.endDate = 'Einddatum moet op of na startdatum zijn';
      }
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Beschrijving mag maximaal 1000 tekens bevatten';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Create dates with default times (12:00 for start, 23:59 for end)
    const startDateTime = new Date(`${formData.startDate}T12:00:00`);
    const endDateTime = formData.endDate 
      ? new Date(`${formData.endDate}T23:59:59`)
      : undefined;

    try {
      setIsLoading(true);

      // Create FormData for file upload
      const formDataObj = new FormData();
      formDataObj.append('title', formData.title);
      formDataObj.append('type', formData.type);
      formDataObj.append('startDate', startDateTime.toISOString());
      formDataObj.append('isPublic', 'true');
      
      if (formData.description) {
        formDataObj.append('description', formData.description);
      }
      if (formData.location) {
        formDataObj.append('location', formData.location);
      }
      if (endDateTime) {
        formDataObj.append('endDate', endDateTime.toISOString());
      }
      if (formData.featuredImage) {
        formDataObj.append('eventImage', formData.featuredImage);
      }

      const createdEvent = await eventService.createEvent(formDataObj);

      toast.success('Succes', 'Evenement staat live!');
      navigate(`/events/${createdEvent.slug || createdEvent.id}`, {
        state: { justCreated: true },
      });
    } catch (error: any) {
      console.error('Failed to create event:', error);
      const message = error.response?.data?.message || error.message || 'Aanmaken evenement mislukt';
      toast.error('Fout', message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="sf-card">
        <div className="px-6 sm:px-7 py-6 border-b border-white/[.08]">
          <div className="sf-eyebrow mb-3">{'// nieuw evenement'}</div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-fg-bright">Nieuw evenement aanmaken</h1>
          <p className="text-fg-muted mt-2">
            Maak een nieuw evenement aan. Het komt direct live te staan — ons team controleert het achteraf even mee.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 sm:p-7 space-y-6">
          {/* Event Name */}
          <div>
            <label htmlFor="title" className="block text-sm font-semibold text-fg-soft mb-2">
              Wat is de naam van het evenement? *
            </label>
            <Input
              id="title"
              name="title"
              type="text"
              required
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Bijvoorbeeld: Lowlands 2025"
              className={`w-full ${errors.title ? '!border-neon-pink/60' : ''}`}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-neon-pink-soft">{errors.title}</p>
            )}
          </div>

          {/* Event Location */}
          <div>
            <label htmlFor="location" className="block text-sm font-semibold text-fg-soft mb-2">
              Locatie *
            </label>
            <div className="relative">
              <MapPin className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neon-pink z-10" />
              <Input
                id="location"
                name="location"
                type="text"
                required
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Bijvoorbeeld: Biddinghuizen"
                className={`w-full pl-10 ${errors.location ? '!border-neon-pink/60' : ''}`}
              />
            </div>
            {errors.location && (
              <p className="mt-1 text-sm text-neon-pink-soft">{errors.location}</p>
            )}
          </div>


          {/* Event Type */}
          <div>
            <label htmlFor="type" className="block text-sm font-semibold text-fg-soft mb-2">
              Type evenement *
            </label>
            <div className="relative">
              <Tag className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neon-cyan z-10" />
              <select
                id="type"
                name="type"
                required
                value={formData.type}
                onChange={handleInputChange}
                className="sf-input pl-10"
              >
                {eventTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Start Date and End Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="startDate" className="block text-sm font-semibold text-fg-soft mb-2">
                Startdatum *
              </label>
              <div className="relative">
                <Input
                  id="startDate"
                  name="startDate"
                  type="date"
                  required
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className={`w-full ${errors.startDate ? '!border-neon-pink/60' : ''}`}
                />
              </div>
              {errors.startDate && (
                <p className="mt-1 text-sm text-neon-pink-soft">{errors.startDate}</p>
              )}
            </div>
            <div>
              <label htmlFor="endDate" className="block text-sm font-semibold text-fg-soft mb-2">
                Einddatum
              </label>
              <div className="relative">
                <Input
                  id="endDate"
                  name="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  className={`w-full ${errors.endDate ? '!border-neon-pink/60' : ''}`}
                />
              </div>
              {errors.endDate && (
                <p className="mt-1 text-sm text-neon-pink-soft">{errors.endDate}</p>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-semibold text-fg-soft mb-2">
              Beschrijving
            </label>
            <textarea
              id="description"
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Vertel meer over het evenement..."
              className={`sf-input resize-y ${errors.description ? '!border-neon-pink/60' : ''}`}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-neon-pink-soft">{errors.description}</p>
            )}
          </div>

          {/* Featured Image Upload */}
          <div>
            <label className="block text-sm font-semibold text-fg-soft mb-2">
              Afbeelding (optioneel)
            </label>
            <FeaturedImageUpload
              onFileSelect={handleFileSelect}
              {...(errors.featuredImage && { error: errors.featuredImage })}
            />
          </div>

          {/* Immutability Notice */}
          <div className="sf-notice-info">
            Let op: na het aanmaken kun je de gegevens van je evenement niet meer zelf
            wijzigen. Controleer alles dus goed.
          </div>

          {/* Submit Buttons */}
          <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3 sm:justify-end pt-5 border-t border-white/[.08]">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/events')}
              disabled={isLoading}
              className="w-full sm:w-auto min-h-[44px] text-base"
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="w-full sm:w-auto min-h-[44px] text-base"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Maken...</span>
                </>
              ) : (
                'Evenement aanmaken'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEventPage;